import api from "./apiClient";
import * as CONST from "../utilities/constants";

export async function getAllCohortsbyTagID(tagId, amsFlavor, modelingEnabled, signalEnabled = false) {
	const response = await api.get(`/siloCohorts/${tagId}`, {
		headers: {
			"UI-Version": 2,
			"AMS-Flavor": amsFlavor,
			"Modeling-Enabled": modelingEnabled ? 1 : 0,
			// Signal audiences are gated independently of modeling: they are an affinity
			// lookup, not a trained model.
			"Signal-Enabled": signalEnabled ? 1 : 0
		}
	});
	return response.json();
}

export async function getTopAutoGenCohortsbyTagID(tagId, amsFlavor) {
	const response = await api.get(`/autogenerated/${tagId}`, {
		headers: {
			"AMS-Flavor": amsFlavor
		}
	});
	return response.json();
}

export async function getCohortbyCohortID(uniqueId, autogenTagId) {
	const response = await api.get(`/cohorts/${uniqueId}`, {
		headers: {
			tagId: parseInt(autogenTagId)
		}
	});
	return response.json();
}

export async function getDemographics(region) {
	const response = await api.get(`/demographics?region=${region}`, { skipAuth: true });
	return response.json();
}

export async function getCategories() {
	const response = await api.get("/categories", { skipAuth: true });
	return response.json();
}

export async function getGARMTaxonomy() {
	const response = await api.get("/garm", { skipAuth: true });
	return response.json();
}

export async function getSentimentTaxonomy() {
	const response = await api.get("/sentiments", { skipAuth: true });
	return response.json();
}

export async function getSyndicatedData(tagId) {
	const response = await api.get("/syndicated", {
		headers: {
			tagId: parseInt(tagId)
		}
	});
	return response.json();
}

export async function getActivationMethods() {
	const response = await api.get("/activationMethods", { skipAuth: true });
	const methods = await response.json();
	// Sort alphabetically by name, but pin "Google Ad Manager" to the top
	return [...methods].sort((a, b) => {
		if (a.name === "Google Ad Manager") return -1;
		if (b.name === "Google Ad Manager") return 1;
		return a.name.localeCompare(b.name);
	});
}

export async function getLocation() {
	const response = await api.get("/location", { skipAuth: true });
	return response.json();
}

export async function createCohort(payload) {
	const response = await api.post("/cohorts", payload);
	return response;
}

// A save carrying a version (ASB-4801) is not re-sent on a 5xx or a dropped connection: if
// the first attempt committed, the re-send carries the version it moved past and is refused as
// "changed by someone else". The user sees the failure instead and reloads.
const versioned = (sent) => (sent ? { retryServerErrors: false } : {});
// A selection loaded without versions maps to {}: that sends none, so it retries as before.
const hasVersions = (versions) => Boolean(versions) && Object.keys(versions).length > 0;

export async function updateCohort(payload, uniqueId, autogenTagId) {
	const response = await api.put(`/cohorts/${uniqueId}`, payload, {
		headers: {
			tagId: parseInt(autogenTagId)
		},
		...versioned((payload?.data || []).some((c) => c && c.version != null)),
	});
	return response;
}

export async function refreshModeledCohort(uniqueId) {
	const response = await api.put(`/cohorts/${uniqueId}/refresh`, {});
	return response;
}

// `versions` ({uniqueId: version} as the list loaded them, ASB-4801): an audience saved by
// someone else since is refused with 409 concurrent_change instead of being overwritten.
export async function bulkSyncCohorts(uniqueIds, action, activationMethods, autogenTagId, versions) {
	const response = await api.put("/cohorts/sync", {
		cohortIds: uniqueIds,
		tagId: parseInt(autogenTagId),
		action: action,
		activationMethods: activationMethods,
		...(versions && { versions }),
	}, versioned(hasVersions(versions)));
	return response.json();
}

export async function bulkEditCohorts(cohortIds, updates, tagId, versions) {
	const response = await api.put("/cohorts/bulk", {
		cohortIds: cohortIds,
		tagId: parseInt(tagId),
		updates: updates,
		...(versions && { versions }),
	}, versioned(hasVersions(versions)));
	return response.json();
}

/** The API refused a save because the audience was saved by someone else since it was loaded (ASB-4801). */
export const isConcurrentChange = (error) =>
	error?.status === 409 && error?.data?.errorCode === "concurrent_change";

/** {uniqueId: version} of selected audiences ({value, version}), for bulkSyncCohorts / bulkEditCohorts. */
export const versionsOf = (audiences) =>
	Object.fromEntries((audiences || []).filter((a) => a.version != null).map((a) => [a.value, a.version]));

export async function exportCohorts(tagId, uniqueIds) {
	const body = { tagId: parseInt(tagId) };
	if (uniqueIds && uniqueIds.length) {
		body.unique_ids = uniqueIds;
	}
	const response = await api.post("/cohorts/bulk/export", body);
	const blob = await response.blob();
	const disposition = response.headers.get("Content-Disposition") || "";
	const match = /filename="?([^";]+)"?/.exec(disposition);
	const filename = match ? match[1] : "audiences_bulk_edit.xlsx";
	const skippedHeader = response.headers.get("X-Bulk-Editor-Skipped");
	const skipped = skippedHeader ? skippedHeader.split(",").filter(Boolean) : [];
	return { blob, filename, skipped };
}

export async function importCohorts(file, dryRun = true) {
	// The silo is read from the file's signed keys server-side, so no tagId is sent.
	const form = new FormData();
	form.append("file", file);
	form.append("dryRun", dryRun ? "true" : "false");
	try {
		// Raw request so the FormData body keeps its multipart boundary (api.post would JSON-encode it).
		const response = await api.request("/cohorts/bulk/import", { method: "POST", body: form });
		return response.json();
	} catch (err) {
		// /cohorts/bulk/import returns 409 with the full plan when the import is rejected.
		if (err && err.status === 409 && err.data) {
			return err.data;
		}
		throw err;
	}
}

export async function bulkDeleteCohorts(cohortIds, tagId) {
	const response = await api.delete("/cohorts/bulk", {
		body: JSON.stringify({
			cohortIds: cohortIds,
			tagId: parseInt(tagId)
		}),
		headers: { "Content-Type": "application/json" }
	});
	return response.json();
}

export async function getCohortSizing(payload) {
	const response = await api.post("/cohorts/size/sync", payload);
	return response;
}

// Reach for a signal audience at an affinity threshold. Unlike modeled audiences -- whose
// whole reach curve is pre-computed into modeled_info.propensity_throttle -- this is a live
// query against the silo's COHORT_URL_AFFINITY, so the caller must debounce it.
export async function getSignalCohortSizing(payload) {
	const response = await api.post("/cohorts/signal/size/sync", payload);
	return response;
}

export async function getSubCohorts(tagId, amsFlavor, modelingEnabled, contextualOnly = false, signalEnabled = false) {
	const params = new URLSearchParams({ subCohortEligible: "true" });
	if (contextualOnly) {
		params.append("isContextual", "true");
	}
	const response = await api.get(`/siloCohorts/${tagId}?${params.toString()}`, {
		headers: {
			"UI-Version": 2,
			"AMS-Flavor": amsFlavor,
			"Modeling-Enabled": modelingEnabled ? 1 : 0,
			"Signal-Enabled": signalEnabled ? 1 : 0
		}
	});
	return response.json();
}